import { Title } from '@solidjs/meta'
import { useSearchParams } from '@solidjs/router'
import { type Component, createSignal, Show } from 'solid-js'
import Container from '~/components/Container'
import Tabs from '~/components/Tabs'
import { TabConfiguracion, TabRegistros } from '~/layers/ui/admin/auditoria'
import type { TabItemType } from '~/types/Tabs'

const KNOWN_TABS = ['registros', 'configuracion'] as const

const tabs: TabItemType[] = [
  { id: 'registros', label: 'Registros', icon: 'material-symbols-light:history' },
  { id: 'configuracion', label: 'Configuración', icon: 'material-symbols-light:settings-outline' }
]

/**
 * Auditoría: qué ha pasado en la empresa y cómo se captura.
 *
 * Dos pestañas porque son dos trabajos distintos: consultar los movimientos
 * (el día a día) y ajustar la conexión y el interruptor de captura (una vez).
 * La pestaña activa viaja en la URL (`?tab=`) para poder recargar y compartir
 * la vista.
 */
const AuditoriaPage: Component = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const sanitizeTab = (tab: string | undefined): string => (tab && (KNOWN_TABS as readonly string[]).includes(tab) ? tab : 'registros')
  const [activeTab, setActiveTab] = createSignal(sanitizeTab(typeof searchParams.tab === 'string' ? searchParams.tab : undefined))

  /**
   * Una pestaña se monta la primera vez que se visita y a partir de ahí se
   * queda montada, sólo oculta: entrar a configurar no dispara la consulta de
   * eventos, y volver a los registros conserva filtros y página.
   */
  const [visitadas, setVisitadas] = createSignal<string[]>([activeTab()])

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setVisitadas(previas => (previas.includes(tab) ? previas : [...previas, tab]))
    setSearchParams({ ...searchParams, tab }, { replace: true })
  }

  const tabBody = (tab: string) => (activeTab() === tab ? 'pt-5' : 'hidden')

  return (
    <Container>
      <Title>Auditoría | Florez Jaramillo</Title>

      <h1 class="text-2xl font-bold text-foreground">Auditoría</h1>
      <p class="mb-4 text-sm text-foreground-subtle">
        Cada creación, edición y borrado queda registrado con su antes y su después, quién lo hizo y desde dónde. Aquí se consultan y se ajusta cómo se capturan.
      </p>

      <Tabs items={tabs} activeTab={activeTab()} onChange={handleTabChange} variant="underline" />

      <Show when={visitadas().includes('registros')}>
        <div class={tabBody('registros')}>
          <TabRegistros />
        </div>
      </Show>

      <Show when={visitadas().includes('configuracion')}>
        <div class={tabBody('configuracion')}>
          <TabConfiguracion />
        </div>
      </Show>
    </Container>
  )
}

export default AuditoriaPage
